import { NgOptimizedImage } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Signal,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../shared/utils/alerta.service';
import { EtapasServicoComponent } from '../../pages-servicos/etapas-servico/etapas-servico.component';
import { EtapaAnexosSicComponent } from './etapa-anexos-sic/etapa-anexos-sic.component';
import { EtapaConfirmacaoSicComponent } from './etapa-confirmacao-sic/etapa-confirmacao-sic.component';
import { EtapaDadosPedidoSicComponent } from './etapa-dados-pedido-sic/etapa-dados-pedido-sic.component';
import { EtapaIdentificacaoSicComponent } from './etapa-identificacao-sic/etapa-identificacao-sic.component';
import { EtapaOrientacoesSicComponent } from './etapa-orientacoes-sic/etapa-orientacoes-sic.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap } from 'rxjs';
import {
  MENSAGEM_DESAFIO_RECUSADO,
  ehRecusaDoDesafio,
} from '../../services/desafio-formulario.service';
import { EtapaId, ErrosServidorSic, PedidoSicStore } from './pedido-sic.store';
import { ScrollNavegacaoService } from '../../../../core/services/scroll-navegacao.service';
import { AuthService } from '../../../../core/services/auth/auth.service';
import {
  PedidoSicPublicoService,
  etapaDoErroSic,
  mensagensDoErroSic,
  montarFormDataPedidoSic,
} from '../../services/pedido-sic-publico.service';

/** A ordem das etapas, para levar o cidadão à PRIMEIRA que tem erro do servidor. */
const ORDEM_ETAPAS: readonly EtapaId[] = ['orientacoes', 'identificacao', 'dados', 'anexos'];

const MENSAGEM_LIMITE =
  'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente — os dados preenchidos foram mantidos.';
const MENSAGEM_CORPO =
  'Os anexos passam do limite de envio. Remova arquivos ou reduza o tamanho deles e tente novamente.';
const MENSAGEM_FALHA =
  'Não foi possível registrar o pedido agora. Tente novamente em instantes — os dados preenchidos foram mantidos.';
const MENSAGEM_SESSAO =
  'Sua sessão expirou. Entre novamente com o seu login para enviar o pedido identificado — os dados preenchidos foram mantidos.';

const TRILHA: readonly BreadcrumbItem[] = [
  { titulo: 'SIC', link: '/sic' },
];

/** Banner de topo da página; as medidas são as da imagem, exigidas pelo NgOptimizedImage. */
const BANNER = {
  caminho: 'assets/images/banners/banner-sic.png',
  largura: 890,
  altura: 318,
};

const ETAPAS: readonly { id: EtapaId; titulo: string }[] = [
  { id: 'orientacoes', titulo: 'Orientações' },
  { id: 'identificacao', titulo: 'Identificação' },
  { id: 'dados', titulo: 'Dados do Pedido' },
  { id: 'anexos', titulo: 'Anexos' },
  { id: 'confirmacao', titulo: 'Confirmação' },
];

@Component({
  selector: 'app-pedido-informacao',
  templateUrl: './pedido-informacao.component.html',
  styleUrls: ['./pedido-informacao.component.css'],
  providers: [PedidoSicStore],
  imports: [
    NgOptimizedImage,
    BreadcrumbComponent,
    LucideModule,
    EtapasServicoComponent,
    EtapaOrientacoesSicComponent,
    EtapaIdentificacaoSicComponent,
    EtapaDadosPedidoSicComponent,
    EtapaAnexosSicComponent,
    EtapaConfirmacaoSicComponent,
  ],
})
export class PedidoInformacaoComponent {
  private readonly alerta = inject(AlertaService);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly publico = inject(PedidoSicPublicoService);

  protected readonly store = inject(PedidoSicStore);

  readonly trilha = TRILHA;
  readonly banner = BANNER;

  readonly etapaAtualId = signal<EtapaId>('orientacoes');

  /** Até onde a pessoa já pode navegar; só avança, nunca retrocede sozinha. */
  private readonly etapaLiberadaId = signal<EtapaId>('orientacoes');

  /** Elemento da trilha de etapas: é por ele que a etapa recém-aberta começa na tela. */
  private readonly trilhaEtapas: Signal<ElementRef<HTMLElement> | undefined> = viewChild(
    'trilhaEtapas',
    { read: ElementRef }
  );

  /** Contador de pedidos de foco no card de acesso; cada incremento leva o cursor até ele. */
  readonly focarLogin = signal(0);

  /**
   * No pedido anônimo não há solicitante a identificar, e com um departamento que não aceita
   * anexos não há o que anexar: as duas etapas saem do fluxo, e a numeração se ajusta.
   */
  readonly etapasVisiveis = computed(() =>
    ETAPAS.filter(
      (etapa) =>
        (etapa.id !== 'identificacao' || !this.store.anonimo()) &&
        (etapa.id !== 'anexos' || this.store.permiteAnexos())
    )
  );

  /** O "Enviar pedido" aparece na última etapa antes da confirmação — anexos ou dados. */
  readonly ultimaEtapaAntesDoEnvio = computed(() => {
    const visiveis = this.etapasVisiveis();

    return visiveis.at(-2)?.id;
  });

  readonly titulos = computed(() => this.etapasVisiveis().map((etapa) => etapa.titulo));

  readonly etapaAtual = computed(() => this.indiceDe(this.etapaAtualId()));

  readonly etapaLiberada = computed(() => this.indiceDe(this.etapaLiberadaId()));

  private readonly identificacao = viewChild(EtapaIdentificacaoSicComponent);
  private readonly dadosPedido = viewChild(EtapaDadosPedidoSicComponent);

  readonly mensagemBloqueio = computed(() =>
    this.etapaLiberadaId() === 'orientacoes'
      ? 'Aceite as orientações e entre com login, gov.br ou solicite anonimamente para liberar'
      : 'Conclua a etapa anterior para liberar'
  );

  readonly podeAvancar = computed(() => {
    switch (this.etapaAtualId()) {
      case 'identificacao':
        return this.store.identificacaoValida();
      case 'dados':
        return this.store.dadosPedidoValidos();
      case 'anexos':
        return this.store.pendenciasAnexos().length === 0;
      default:
        return true;
    }
  });

  constructor() {
    // O desafio de formulário é pedido ao abrir o wizard: envelhece enquanto o cidadão percorre
    // as etapas e chega ao envio já com a idade mínima que a API exige.
    this.store.desafio.carregar();

    let primeiraEtapa = true;

    // Trocar de etapa é uma ação de navegação: a nova etapa abre a partir do seu topo,
    // sem rolagem animada. A abertura da página fica de fora — ali o topo é o da rota.
    effect(() => {
      this.etapaAtualId();

      if (primeiraEtapa) {
        primeiraEtapa = false;
        return;
      }

      afterNextRender(() => this.rolarParaEtapa(), { injector: this.injector });
    });
  }

  private rolarParaEtapa(): void {
    this.scrollNavegacao.aoInicioDaEtapa(this.trilhaEtapas()?.nativeElement);
  }

  irParaEtapa(indice: number): void {
    if (indice > this.etapaLiberada()) return;

    const etapa = this.etapasVisiveis()[indice];

    if (etapa) {
      this.etapaAtualId.set(etapa.id);
    }
  }

  avancar(): void {
    if (!this.podeAvancar()) {
      this.destacarPendencias();
      return;
    }

    this.abrir(this.etapasVisiveis()[this.etapaAtual() + 1]?.id);
  }

  voltar(): void {
    const anterior = this.etapasVisiveis()[this.etapaAtual() - 1];

    if (anterior) {
      this.etapaAtualId.set(anterior.id);
    }
  }

  /**
   * Confirma e envia: UMA requisição `multipart/form-data`, só depois do modal, com o botão
   * bloqueado enquanto ela está no ar — um segundo clique não gera um segundo pedido. Os erros
   * de validação do servidor voltam à etapa a que pertencem.
   */
  async enviar(): Promise<void> {
    if (this.store.enviando()) return;

    const pendencia = this.primeiraPendencia();

    if (pendencia) {
      this.etapaAtualId.set(pendencia);
      afterNextRender(() => this.destacarPendencias(), { injector: this.injector });
      return;
    }

    // O caminho identificado precisa da sessão: sem ela o servidor trataria o pedido como
    // anônimo e recusaria os dados de contato. Melhor dizer antes de enviar.
    if (this.store.identificado() && !this.auth.currentUserValue?.token) {
      await this.alerta.aviso({ titulo: 'Entre com o seu login', texto: MENSAGEM_SESSAO });
      this.etapaAtualId.set('orientacoes');
      this.focarLogin.update((vezes) => vezes + 1);
      return;
    }

    const confirmado = await this.alerta.confirmar({
      titulo: 'Enviar pedido de informação?',
      texto:
        'O pedido será registrado no e-SIC e receberá um número de protocolo. Depois do envio o ' +
        'conteúdo não pode mais ser alterado.',
      confirmar: 'Sim, enviar',
      cancelar: 'Revisar antes',
      tom: 'info',
    });

    if (!confirmado || this.store.enviando()) return;

    this.store.enviando.set(true);
    this.store.errosServidor.set({});

    const anonimo = this.store.anonimo();
    const identificacao = this.store.identificacao();
    const pedido = this.store.dadosPedido();

    const corpo = montarFormDataPedidoSic({
      anonimo,
      aceite: this.store.orientacoesAceitas(),
      codDepartamentoDep: pedido.codDepartamento ?? 0,
      assunto: pedido.assunto,
      descricao: pedido.descricao,
      identificacao: anonimo
        ? null
        : {
            nome: identificacao.nomeCompleto,
            cpf: identificacao.cpf,
            email: identificacao.email,
            telefone: identificacao.telefone,
            tipoSolicitante: identificacao.tipoSolicitante,
            logradouro: identificacao.logradouro,
            numero: identificacao.numero,
            complemento: identificacao.complemento,
            bairro: identificacao.bairro,
            cidade: identificacao.cidade,
            uf: identificacao.uf,
            cep: identificacao.cep,
          },
      formaRecebimento: pedido.formaRecebimento,
      protocoloVinculado: pedido.temProtocoloAnterior ? pedido.protocoloAnterior : '',
      receberAvisos: pedido.receberAvisos,
      anexos: this.store.permiteAnexos() ? this.store.anexos().map((anexo) => anexo.arquivo) : [],
      honeypot: this.store.desafio.honeypot(),
    });

    // Se a carga do desafio falhou, `paraEnvio` pede outro agora; falhando de novo, cai no erro
    // genérico de sempre.
    this.store.desafio
      .paraEnvio()
      .pipe(
        switchMap((desafio) => this.publico.abrir(corpo, anonimo, desafio)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.store.enviando.set(false))
      )
      .subscribe({
        next: (resultado) => {
          this.store.resultado.set(resultado);
          this.etapaLiberadaId.set('confirmacao');
          this.etapaAtualId.set('confirmacao');
        },
        error: (erro: HttpErrorResponse) => void this.tratarErroEnvio(erro),
      });
  }

  /**
   * Cada erro vai para a sua etapa: 400 de campo marca a etapa e leva até a primeira com erro;
   * 422 (departamento que exige login, órgão sem prazo publicado) é mensagem ao cidadão, na
   * etapa de dados; 413 e 429 são avisos, sem perder o que foi preenchido.
   */
  private async tratarErroEnvio(erro: HttpErrorResponse): Promise<void> {
    const mensagens = mensagensDoErroSic(erro);

    // O desafio de formulário foi recusado (vencido num wizard demorado, reusado): pede outro, e
    // o cidadão envia de novo da mesma etapa — todas as etapas e arquivos ficam.
    if (ehRecusaDoDesafio(erro)) {
      this.store.desafio.renovar();
      await this.alerta.aviso({ titulo: 'Envie novamente', texto: MENSAGEM_DESAFIO_RECUSADO });
      return;
    }

    if (erro.status === 429) {
      await this.alerta.aviso({ titulo: 'Aguarde um instante', texto: MENSAGEM_LIMITE });
      return;
    }

    if (erro.status === 413) {
      this.store.errosServidor.set({ anexos: [MENSAGEM_CORPO] });
      this.etapaAtualId.set(this.store.permiteAnexos() ? 'anexos' : 'dados');
      return;
    }

    if (erro.status === 422) {
      this.store.errosServidor.set({ dados: mensagens });
      this.etapaAtualId.set('dados');
      await this.alerta.aviso({ titulo: 'Não foi possível enviar', texto: mensagens[0] ?? MENSAGEM_FALHA });
      return;
    }

    if (erro.status === 400 && mensagens.length) {
      // Identificado cujo token expirou no meio do caminho: o servidor tomou o pedido por
      // anônimo e recusou o contato. A saída é entrar de novo, não corrigir campo.
      if (this.store.identificado() && mensagens.some((mensagem) => mensagem.includes('pedido anônimo'))) {
        this.auth.logout();
        await this.alerta.aviso({ titulo: 'Sessão encerrada', texto: MENSAGEM_SESSAO });
        this.etapaAtualId.set('orientacoes');
        this.focarLogin.update((vezes) => vezes + 1);
        return;
      }

      const porEtapa: Partial<Record<EtapaId, string[]>> = {};

      for (const mensagem of mensagens) {
        const etapa = etapaDoErroSic(mensagem);
        porEtapa[etapa] = [...(porEtapa[etapa] ?? []), mensagem];
      }

      this.store.errosServidor.set(porEtapa as ErrosServidorSic);

      const visiveis = new Set(this.etapasVisiveis().map((etapa) => etapa.id));
      const destino = ORDEM_ETAPAS.find((etapa) => porEtapa[etapa]?.length && visiveis.has(etapa)) ?? 'dados';

      this.etapaAtualId.set(destino);
      return;
    }

    await this.alerta.erro({ titulo: 'Não foi possível enviar', texto: MENSAGEM_FALHA });
  }

  /** A primeira etapa visível com pendência local, antes de qualquer envio. */
  private primeiraPendencia(): EtapaId | null {
    if (this.store.identificado() && !this.store.identificacaoValida()) return 'identificacao';
    if (!this.store.dadosPedidoValidos()) return 'dados';
    if (this.store.permiteAnexos() && this.store.pendenciasAnexos().length) return 'anexos';

    return null;
  }

  continuarIdentificado(): void {
    this.store.entrarIdentificado();
    this.iniciarPedido('identificacao');
  }

  autenticar(): void {
    this.store.sessaoIniciada.set(true);
    this.continuarIdentificado();
  }

  acessarGov(): void {
    this.store.entrarPorGov();
    this.iniciarPedido('identificacao');
  }

  /** Sem identificação a coletar, o pedido anônimo pula direto para os dados do pedido. */
  seguirAnonimo(): void {
    this.store.entrarAnonimo();
    this.iniciarPedido('dados');
  }

  /** O "Abrir pedido" do topo leva ao card de acesso: a etapa só é liberada pela escolha feita lá. */
  irParaLogin(): void {
    this.etapaAtualId.set('orientacoes');
    this.focarLogin.update((vezes) => vezes + 1);
  }

  encerrarSessao(): void {
    this.reiniciar();
  }

  async cancelar(): Promise<void> {
    const confirmado = await this.alerta.confirmar({
      titulo: 'Cancelar pedido de informação?',
      texto: 'Os dados preenchidos até aqui serão descartados.',
      confirmar: 'Sim, cancelar',
      cancelar: 'Continuar preenchendo',
    });

    if (confirmado) {
      this.reiniciar();
    }
  }

  reiniciar(): void {
    this.store.limpar();
    this.etapaAtualId.set('orientacoes');
    this.etapaLiberadaId.set('orientacoes');
    this.focarLogin.set(0);
  }

  /** Primeira etapa depois das orientações, escolhida pelo modo de acesso. */
  private iniciarPedido(id: EtapaId): void {
    // Zera o pedido de foco para o card da etapa seguinte não roubar o cursor.
    this.focarLogin.set(0);
    this.abrir(id);
  }

  private abrir(id: EtapaId | undefined): void {
    if (!id) return;

    this.etapaAtualId.set(id);

    if (this.indiceDe(id) > this.etapaLiberada()) {
      this.etapaLiberadaId.set(id);
    }
  }

  /** Posição da etapa na lista visível; etapa oculta cai no início. */
  private indiceDe(id: EtapaId): number {
    return Math.max(
      this.etapasVisiveis().findIndex((etapa) => etapa.id === id),
      0
    );
  }

  private destacarPendencias(): void {
    if (this.etapaAtualId() === 'identificacao') {
      this.identificacao()?.destacarPendencias();
      return;
    }

    if (this.etapaAtualId() === 'dados') {
      this.dadosPedido()?.destacarPendencias();
    }
  }
}
